import { DomainError } from '../errors/domain-error';

export const CREATOR_CUSTOM_DOMAIN_MAX = 253;
export const CREATOR_DOMAIN_VERIFICATION_PREFIX = 'loamify-verify=';
/** Default max hostnames per creator until plan entitlements land. */
export const CREATOR_DOMAIN_MAX_PER_CREATOR = 10;
/** Pending domains expire after 7 days (NFR working limits). */
export const CREATOR_DOMAIN_PENDING_TTL_MS = 7 * 24 * 60 * 60 * 1000;
export const DEFAULT_CREATOR_DOMAIN_CNAME_TARGET = 'domains.loamify.io';
export const DEFAULT_CREATOR_DOMAIN_MARKETING_MAIL_HOST = 'news.loamify.io';

/** @deprecated Use CREATOR_CUSTOM_DOMAIN_MAX */
export const COMMUNITY_CUSTOM_DOMAIN_MAX = CREATOR_CUSTOM_DOMAIN_MAX;
/** @deprecated Use CREATOR_DOMAIN_VERIFICATION_PREFIX */
export const COMMUNITY_DOMAIN_VERIFICATION_PREFIX = CREATOR_DOMAIN_VERIFICATION_PREFIX;

const HOSTNAME_PATTERN =
  /^(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?$/;

const BLOCKED_HOST_EXACT = new Set(['loamify.io', 'loamify.com', 'localhost']);
const BLOCKED_HOST_SUFFIXES = ['.loamify.io', '.loamify.com', '.localhost'];

/** Common multi-part public suffixes (PSF lite — ops can override later). */
const MULTI_PART_PUBLIC_SUFFIXES = [
  'co.uk',
  'org.uk',
  'ac.uk',
  'gov.uk',
  'com.au',
  'net.au',
  'org.au',
  'co.nz',
  'co.jp',
  'com.br',
  'co.in',
  'com.mx',
  'co.za',
  'com.sg',
  'com.hk',
  'co.kr',
];

export const CREATOR_DOMAIN_TARGET_TYPES = [
  'creator_site',
  'creator_profile',
  'circle_landing',
  'newsletter_publication',
  'events_calendar',
  'course_page',
  'membership_page',
  'offer_page',
  'event_page',
  'marketing_form',
] as const;

export type CreatorDomainTargetType = (typeof CREATOR_DOMAIN_TARGET_TYPES)[number];

/** Targets that require a real ownership table before bind is allowed. */
export const CREATOR_DOMAIN_UNSUPPORTED_TARGETS: CreatorDomainTargetType[] = [
  'marketing_form',
];

export const CREATOR_DOMAIN_REQUIRED_TARGETS: CreatorDomainTargetType[] = [
  'creator_site',
  'creator_profile',
  'circle_landing',
  'newsletter_publication',
  'events_calendar',
  'course_page',
  'membership_page',
];

export const CREATOR_DOMAIN_LITE_TARGETS: CreatorDomainTargetType[] = [
  'offer_page',
  'event_page',
  'marketing_form',
];

export const CREATOR_DOMAIN_TARGET_LABELS: Record<CreatorDomainTargetType, string> = {
  creator_site: 'Creator website',
  creator_profile: 'Public profile',
  circle_landing: 'Circle landing',
  newsletter_publication: 'Newsletter publication',
  events_calendar: 'Events calendar',
  course_page: 'Course page',
  membership_page: 'Membership page',
  offer_page: 'Offer page',
  event_page: 'Single event',
  marketing_form: 'Marketing form (coming soon)',
};

export type CreatorDomainRedirectMode = 'none' | 'to_www' | 'to_apex';

export function isCreatorDomainTargetType(value: string): value is CreatorDomainTargetType {
  return (CREATOR_DOMAIN_TARGET_TYPES as readonly string[]).includes(value);
}

export function isUnsupportedCreatorDomainTarget(value: CreatorDomainTargetType): boolean {
  return CREATOR_DOMAIN_UNSUPPORTED_TARGETS.includes(value);
}

/** Normalize hostname to lowercase ASCII (punycode); preserves `www.`. */
export function normalizeCreatorHostname(value: string | null | undefined): string | null {
  const trimmed = value?.trim().toLowerCase().replace(/\.$/, '');
  if (!trimmed) return null;
  try {
    // URL hostname conversion yields punycode (xn--) for IDN labels.
    const ascii = new URL(`http://${trimmed}`).hostname.toLowerCase();
    return ascii || null;
  } catch {
    return trimmed;
  }
}

/** Legacy Circle helper: strip www for single-host matching. */
export function normalizeCommunityHostname(value: string | null | undefined): string | null {
  const normalized = normalizeCreatorHostname(value);
  if (!normalized) return null;
  return normalized.startsWith('www.') ? normalized.slice(4) : normalized;
}

export function stripWwwHostname(hostname: string): string {
  return hostname.startsWith('www.') ? hostname.slice(4) : hostname;
}

export function isWwwHostname(hostname: string): boolean {
  return hostname.startsWith('www.');
}

function matchesMultiPartSuffix(hostname: string): string | null {
  for (const suffix of MULTI_PART_PUBLIC_SUFFIXES) {
    if (hostname === suffix || hostname.endsWith(`.${suffix}`)) {
      return suffix;
    }
  }
  return null;
}

/**
 * Apex detection with multi-part public suffix awareness (example.co.uk).
 * `www.` hosts are never apex.
 */
export function isApexHostname(hostname: string): boolean {
  if (isWwwHostname(hostname)) return false;
  const multi = matchesMultiPartSuffix(hostname);
  if (multi) {
    const withoutSuffix = hostname.slice(0, -(multi.length + 1));
    if (!withoutSuffix || withoutSuffix.includes('.')) return false;
    return true;
  }
  return hostname.split('.').length === 2;
}

export function assertCreatorCustomHostname(
  hostname: string,
  options?: { cnameTarget?: string | null },
): void {
  const normalized = normalizeCreatorHostname(hostname);
  if (!normalized) {
    throw new DomainError('Custom domain hostname is required');
  }
  if (normalized.length > CREATOR_CUSTOM_DOMAIN_MAX) {
    throw new DomainError('Custom domain hostname is too long');
  }
  if (!HOSTNAME_PATTERN.test(normalized)) {
    throw new DomainError('Enter a valid custom domain hostname');
  }
  if (normalized.includes('..') || normalized.includes('_')) {
    throw new DomainError('Custom domain hostname is invalid');
  }
  if (BLOCKED_HOST_EXACT.has(normalized)) {
    throw new DomainError('Use a domain you own, not a LOAMIFY platform hostname');
  }
  if (BLOCKED_HOST_SUFFIXES.some((suffix) => normalized.endsWith(suffix))) {
    throw new DomainError('Use a domain you own, not a LOAMIFY platform hostname');
  }
  const cnameTarget = creatorDomainCnameTarget(options?.cnameTarget);
  if (normalized === cnameTarget || normalized.endsWith(`.${cnameTarget}`)) {
    throw new DomainError('Use a domain you own, not a LOAMIFY platform hostname');
  }
}

/** @deprecated Use assertCreatorCustomHostname */
export function assertCommunityCustomHostname(hostname: string): void {
  assertCreatorCustomHostname(hostname);
}

export function creatorDomainTxtRecord(token: string): string {
  return `${CREATOR_DOMAIN_VERIFICATION_PREFIX}${token}`;
}

/** @deprecated Use creatorDomainTxtRecord */
export function communityDomainTxtRecord(token: string): string {
  return creatorDomainTxtRecord(token);
}

export function creatorDomainVerificationHost(hostname: string): string {
  return `_loamify.${hostname}`;
}

/** @deprecated Use creatorDomainVerificationHost */
export function communityDomainVerificationHost(hostname: string): string {
  return creatorDomainVerificationHost(hostname);
}

/** Pure helper — pass configured target from API/env; defaults to platform CNAME. */
export function creatorDomainCnameTarget(configured?: string | null): string {
  const value = configured?.trim().toLowerCase();
  return value || DEFAULT_CREATOR_DOMAIN_CNAME_TARGET;
}

/** Pure helper — pass configured IPs from API/env. */
export function creatorDomainApexIps(configured?: string | null): string[] {
  const raw = configured?.trim();
  if (!raw) return [];
  return raw
    .split(',')
    .map((part) => part.trim())
    .filter(Boolean);
}

/**
 * Apex DNS is satisfied only when configured edge IPs match A records.
 * Empty IP config fails closed (do not activate on TXT alone).
 */
export function isApexDnsSatisfied(params: {
  apexIps: string[];
  aRecords: string[];
}): boolean {
  if (params.apexIps.length === 0) return false;
  return params.aRecords.some((ip) => params.apexIps.includes(ip));
}

/** True when redirect URL is https and host is the hostname or its www/apex pair. */
export function isAllowedCreatorDomainRedirect(
  redirectTo: string,
  hostname: string,
): boolean {
  try {
    const url = new URL(redirectTo);
    if (url.protocol !== 'https:') return false;
    const host = url.hostname.toLowerCase();
    const apex = stripWwwHostname(hostname.toLowerCase());
    return host === hostname.toLowerCase() || host === apex || host === `www.${apex}`;
  } catch {
    return false;
  }
}

/** Pairing policy: apex is canonical; www redirects to apex. */
export function pairWwwRedirectModes(): {
  apex: CreatorDomainRedirectMode;
  www: CreatorDomainRedirectMode;
} {
  return { apex: 'none', www: 'to_apex' };
}

/** Constant-time secret compare for TLS webhooks (Node Buffer API). */
export function creatorDomainSecretsMatch(
  provided: string | undefined,
  expected: string | undefined,
): boolean {
  if (!provided || !expected) return false;
  if (provided.length !== expected.length) return false;
  let mismatch = 0;
  for (let i = 0; i < provided.length; i += 1) {
    mismatch |= provided.charCodeAt(i) ^ expected.charCodeAt(i);
  }
  return mismatch === 0;
}

export function subdomainPresetForTarget(target: CreatorDomainTargetType): string {
  switch (target) {
    case 'circle_landing':
      return 'community';
    case 'newsletter_publication':
      return 'newsletter';
    case 'events_calendar':
      return 'events';
    case 'course_page':
      return 'learn';
    case 'membership_page':
      return 'members';
    case 'creator_profile':
      return 'link';
    case 'offer_page':
      return 'offer';
    case 'event_page':
      return 'meetup';
    case 'marketing_form':
      return 'join';
    case 'creator_site':
    default:
      return 'www';
  }
}

export function creatorDomainMarketingMailHost(configured?: string | null): string {
  const value = configured?.trim().toLowerCase();
  return value || DEFAULT_CREATOR_DOMAIN_MARKETING_MAIL_HOST;
}

export type MarketingSendingDnsRecord = {
  type: 'TXT' | 'CNAME';
  host: string;
  value: string;
  purpose: 'SPF' | 'DKIM' | 'DMARC';
};

export function marketingSendingDnsRecords(
  hostname: string,
  mailHost?: string | null,
): {
  suggestedFromDomain: string;
  records: MarketingSendingDnsRecord[];
} {
  const root = stripWwwHostname(hostname.toLowerCase());
  const includeHost = creatorDomainMarketingMailHost(mailHost);
  return {
    suggestedFromDomain: root,
    records: [
      {
        type: 'TXT',
        host: root,
        value: `v=spf1 include:${includeHost} ~all`,
        purpose: 'SPF',
      },
      {
        type: 'CNAME',
        host: `loamify._domainkey.${root}`,
        value: `loamify._domainkey.${includeHost}`,
        purpose: 'DKIM',
      },
      {
        type: 'TXT',
        host: `_dmarc.${root}`,
        value: 'v=DMARC1; p=none; rua=mailto:dmarc@loamify.io',
        purpose: 'DMARC',
      },
    ],
  };
}

export type MarketingSendingAuthStatus = 'green' | 'partial' | 'missing';

export function marketingSendingAuthStatus(params: {
  spfOk: boolean;
  dkimOk: boolean;
  dmarcOk: boolean;
}): MarketingSendingAuthStatus {
  if (params.spfOk && params.dkimOk && params.dmarcOk) return 'green';
  if (params.spfOk || params.dkimOk || params.dmarcOk) return 'partial';
  return 'missing';
}

export type MarketingFromDomainState =
  | 'platform_default'
  | 'host_pending'
  | 'dns_pending'
  | 'verified';

export function marketingFromDomainState(params: {
  hasNewsletterHost: boolean;
  hostActive: boolean;
  authStatus?: MarketingSendingAuthStatus | null;
}): MarketingFromDomainState {
  if (!params.hasNewsletterHost) return 'platform_default';
  if (!params.hostActive) return 'host_pending';
  if (params.authStatus === 'green') return 'verified';
  return 'dns_pending';
}
